import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { AccountsItems } from '../models/Accounts';
import * as DomicilappService from '../services/DomicilappService';

// 1. Definimos la interfaz del estado y sus métodos
interface StoreContextType {
    storeId: string;
    setStoreId: (id: string | null) => void;
    storeDetails: AccountsItems;
    setStoreDetails: (details: AccountsItems | null) => void;
    clearStore: () => void;
    // Listado de tiendas del usuario (storeProvider). Vive aquí, y no en
    // cada componente que lo necesite, para que el Sidebar, la página de
    // creación, o cualquier otro lugar, siempre vean el mismo dato.
    stores: AccountsItems[];
    storesLoading: boolean;
    // Recarga el listado desde el backend y deja seleccionada:
    // - selectId, si se pasa (ej. la tienda que se acaba de crear)
    // - si no, la que ya estaba activa (storeId), si sigue existiendo
    // - si no, la primera del listado
    refreshStores: (selectId?: string | number) => Promise<void>;
}

// 2. Creamos el contexto
const StoreContext = createContext<StoreContextType | undefined>(undefined);

// Key única para localStorage
const STORAGE_KEY = 'admin_selected_store_id';
const STORAGE_KEY_DETAILS = 'store_details';

export const StoreProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    // Inicializamos el estado leyendo directamente de localStorage para evitar parpadeos
    const [storeId, setStoreIdState] = useState<string>(() => {
        return localStorage.getItem(STORAGE_KEY) ?? "";
    });

    const [storeDetails, setStoreDetailsState] = useState<AccountsItems>(() => {
        const raw = localStorage.getItem(STORAGE_KEY_DETAILS);
        return raw ? JSON.parse(raw) : ({} as AccountsItems);
    });

    const [stores, setStores] = useState<AccountsItems[]>([]);
    const [storesLoading, setStoresLoading] = useState(false);

    // refreshStores necesita leer el storeId más reciente sin que su propia
    // identidad cambie cada vez que storeId cambia (evita loops si se llama
    // desde un useEffect). Se sincroniza vía ref en vez de por closure.
    const storeIdRef = useRef(storeId);
    useEffect(() => {
        storeIdRef.current = storeId;
    }, [storeId]);

    // Función para cambiar la cuenta activa
    const setStoreId = (id: string | null) => {
        if (id) {
            localStorage.setItem(STORAGE_KEY, id);
        } else {
            localStorage.removeItem(STORAGE_KEY);
        }
        setStoreIdState(id ?? ""); // Esto provocará que todos los componentes suscritos se re-rendericen al instante
    };

    // Función para cambiar la cuenta activa
    const setStoreDetails = (details: AccountsItems | null) => {
        if (details) {
            localStorage.setItem(STORAGE_KEY_DETAILS, JSON.stringify(details));
        } else {
            localStorage.removeItem(STORAGE_KEY_DETAILS);
        }
        setStoreDetailsState(details ?? ({} as AccountsItems)); // Esto provocará que todos los componentes suscritos se re-rendericen al instante
    };

    const clearStore = () => {
        setStoreId(null);
        setStoreDetails(null);
        setStores([]);
    };

    const refreshStores = useCallback(async (selectId?: string | number) => {
        setStoresLoading(true);
        try {
            const response = await DomicilappService.getStoresByUser();
            const items: AccountsItems[] = response.data.items ?? [];
            setStores(items);

            if (items.length === 0) return;

            // Prioridad: la que me pidan seleccionar > la que ya estaba activa > la primera.
            const targetId = selectId ?? storeIdRef.current;
            const active = items.find((s) => String(s.id) === String(targetId)) ?? items[0];

            setStoreId(String(active.id));
            setStoreDetails(active);
        } finally {
            setStoresLoading(false);
        }
    }, []);

    return (
        <StoreContext.Provider value={{
            storeId, setStoreId, storeDetails, setStoreDetails, clearStore,
            stores, storesLoading, refreshStores,
        }}>
            {children}
        </StoreContext.Provider>
    );
};

// 3. Custom Hook estilo "Provider.of(context)" de Flutter
export const useStore = (): StoreContextType => {
    const context = useContext(StoreContext);
    if (!context) {
        throw new Error('useStore debe ser usado dentro de un <StoreProvider>');
    }
    return context;
};